import { useCallback, useEffect, useRef, useState } from 'react';
import { AntAPI } from 'ante-react-kit';
import useDebounced from '../shared/useDebounced';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 180;

function mergeById(a, b) {
    const seen = new Set();
    const out = [];
    for (const r of a) {
        if (r && r.id != null && !seen.has(r.id)) {
            seen.add(r.id);
            out.push(r);
        }
    }
    for (const r of b) {
        if (r && r.id != null && !seen.has(r.id)) {
            seen.add(r.id);
            out.push(r);
        }
    }
    return out;
}

function hasIdentity(r) {
    const name = r && (r.entity_full_name || r.entity_name);
    return !!(name && String(name).trim());
}

export default function useEmployeeSearch({ enabled, extraConditions }) {
    const [query, setQuery] = useState('');
    const debounced = useDebounced(query, DEBOUNCE_MS);
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef(null);
    const seqRef = useRef(0);
    if (!apiRef.current) apiRef.current = new AntAPI('entity', 'BusinessEntity');

    const extraKey = JSON.stringify(extraConditions || {});

    const load = useCallback(
        async (pIdx, append, q, extra) => {
            if (!enabled) return;
            const seq = ++seqRef.current;
            setLoading(true);
            try {
                const res = await apiRef.current.getList({
                    is_active: 1,
                    type: 'Employee',
                    ...(extra || {}),
                    searchText: q || undefined,
                    ordering: [{ name: 'entity_name', type: 'asc' }],
                    pageIndex: pIdx,
                    pageSize: PAGE_SIZE
                });
                if (seq !== seqRef.current) return;
                const serverRows = ((res && res.rows) || []).filter(hasIdentity);
                setRows((prev) => (append ? mergeById(prev, serverRows) : serverRows));
                setTotalCount((res && res.totalCount) || 0);
            } finally {
                if (seq === seqRef.current) setLoading(false);
            }
        },
        [enabled]
    );

    useEffect(() => {
        if (!enabled) return;
        setPageIndex(0);
        load(0, false, debounced, JSON.parse(extraKey));
    }, [debounced, extraKey, enabled, load]);

    const loadMore = useCallback(() => {
        if (loading) return;
        if (rows.length >= totalCount) return;
        const next = pageIndex + 1;
        setPageIndex(next);
        load(next, true, debounced, JSON.parse(extraKey));
    }, [loading, rows.length, totalCount, pageIndex, load, debounced, extraKey]);

    const reset = useCallback(() => {
        setQuery('');
        setRows([]);
        setTotalCount(0);
        setPageIndex(0);
    }, []);

    return { query, setQuery, rows, totalCount, loading, loadMore, reset };
}
